import { CalendarItemUnion } from '$tablet-app/src/app/calendar/types';
import NewButton from '$tablet-app/src/app/components/Button/NewButton';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import Row from '$tablet-app/src/app/components/Row';
import Text from '$tablet-app/src/app/components/Text';
import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import { Button } from '@akin-home/buttons/native';
import { useBool } from '@akin-home/utils/react';
import { addDays, format, subDays } from 'date-fns';
import React, { FC } from 'react';
import { useWindowDimensions } from 'react-native';
import { Overlay } from 'react-native-elements';
import styled from 'styled-components/native';
import { useGetCalendarItemsForDay } from '../../hooks';
import CalendarDayBreakdownSchedule from '../CalendarDayBreakdownSchedule';
import CalendarDayNoEvents from '../CalendarDayNoEvents';
import CalendarMonthPicker from '../CalendarMonthPicker';
import CalendarSheet from '../CalendarSheet';
import CalendarSheetRightColumn from '../CalendarSheet/CalendarSheetRightColumn';

interface Props {
  active_date_string?: string;

  items?: CalendarItemUnion[];

  onPressItem?: (item: CalendarItemUnion) => void;

  onPressCreateItem?: (params?: { start?: string }) => void;

  onSelectDate?: (date: string) => void;
}

const CalendarScreenPortraitView: FC<Props> = (props) => {
  const { onPressItem, onPressCreateItem } = props;

  const [active_date_string, setActiveDate] = useStateStore((state) => [
    state.calendar.active_date_string,
    state.calendar.setActiveDate,
  ]);

  const { itemsForDay, isLoading } =
    useGetCalendarItemsForDay(active_date_string);

  const dayHasEvents = itemsForDay && itemsForDay.length > 0;

  const onPressCreate = () => {
    onPressCreateItem?.({ start: active_date_string });
  };

  const goToPreviousDay = () => {
    setActiveDate(subDays(new Date(active_date_string), 1).toISOString());
  };

  const goToNextDay = () => {
    setActiveDate(addDays(new Date(active_date_string), 1).toISOString());
  };

  const [monthPickerVisible, showMonthPicker, hideMonthPicker] = useBool(false);
  const { width } = useWindowDimensions();

  return (
    <CalendarSheet style={{ flexDirection: 'column' }}>
      <CalendarSheetRightColumn>
        <DayHeadingRow>
          <Button
            icon="chevron-left"
            variant="secondary"
            onPress={goToPreviousDay}
          />

          <Button
            variant="text"
            icon={'chevron-down'}
            style={{ flexDirection: 'row-reverse' }}
            onPress={showMonthPicker}
          >
            <Text variant="p" weight="semibold">
              {active_date_string
                ? format(new Date(active_date_string), 'eeee d MMM')
                : null}
            </Text>
          </Button>

          <Button
            icon="chevron-right"
            variant="secondary"
            onPress={goToNextDay}
          />
        </DayHeadingRow>

        {isLoading ? (
          <FullScreenLoader />
        ) : dayHasEvents ? (
          <CalendarDayBreakdownSchedule
            items={itemsForDay}
            onPressItem={onPressItem}
          />
        ) : (
          <CalendarDayNoEvents />
        )}

        {onPressCreateItem ? (
          <CreateButtonContainer pointerEvents="box-none">
            <NewButton
              size="large"
              icon="plus"
              onPress={onPressCreate}
              testID="create-calendar-item-button"
            >
              Create event
            </NewButton>
          </CreateButtonContainer>
        ) : null}
      </CalendarSheetRightColumn>

      <Overlay
        isVisible={monthPickerVisible}
        onBackdropPress={hideMonthPicker}
        onDismiss={hideMonthPicker}
        overlayStyle={{
          maxWidth: Math.min(width, 455),
          minWidth: Math.min(0.95 * width, 455),
          maxHeight: '100%',
          borderRadius: 10,
        }}
      >
        <CalendarMonthPicker
          selected_date_string={active_date_string}
          focus_date_string={active_date_string}
          onPressDay={(day) => {
            setActiveDate(day);
            hideMonthPicker();
          }}
        />
      </Overlay>
    </CalendarSheet>
  );
};

const DayHeadingRow = styled(Row)`
  padding: 10px;
  padding-bottom: 0;
  gap: 16px;

  justify-content: center;
  align-items: center;
`;
const CreateButtonContainer = styled.View`
  position: absolute;
  bottom: 10px;
  width: 100%;
  flex-direction: row;
  justify-content: center;
`;

export default CalendarScreenPortraitView;
